import { Avatar, Button, IconButton } from '@material-ui/core';
import type { Theme } from '@material-ui/core/styles';
import {
  createMuiTheme,
  createStyles,
  makeStyles,
  ThemeProvider,
} from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import type { UserProfileUpdateData } from '@mogul/api-client';
import { DefaultAvatar } from '@mogul/shared';
import React from 'react';

import {
  Button as CustomButton,
  Colors,
} from '../legacy/button/Button.component';
import styles from './Profile.module.css';

const customTheme = createMuiTheme({
  palette: {
    primary: {
      main: '#7E8CB4',
    },
    secondary: {
      main: '#8595c0',
    },
  },
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      '& > *': {
        margin: theme.spacing(1),
      },
    },
    addButton: {
      '&:hover': {
        backgroundColor: '#7e92c7',
      },
      '&:disabled': {
        display: 'none',
      },
      position: 'relative',
      top: -56,
      left: 100,
      width: theme.spacing(3),
      height: theme.spacing(3),
      backgroundColor: '#8595c0',
    },
    addIcon: {
      position: 'relative',
      top: -2,
      width: theme.spacing(2.5),
      height: theme.spacing(2.5),
      color: 'white',
      marginBottom: '4px',
    },
    profileImage: {
      margin: 30,
      border: '5px #3d475c solid',
      width: theme.spacing(12),
      height: theme.spacing(12),
    },
    buttons: {
      width: '100%',
      height: theme.spacing(5),
      color: 'white',
    },
  }),
);

interface Props {
  loading: boolean;
  name: string;
  profileImageSrc?: string;
  onUploadImage: (imageFile: File) => Promise<void>;
  onUpdateProfile: (data: UserProfileUpdateData) => Promise<void>;
  onClickChangePassword: () => void;
  mfaActive: boolean;
  onInitMfa: () => void;
  onDisableMfa: () => void;
  error?: string;
}

export const ProfileComponent: React.FC<Props> = props => {
  const {
    loading,
    mfaActive,
    onInitMfa,
    onDisableMfa,
    onClickChangePassword,
    onUpdateProfile,
  } = props;

  const classes = useStyles();

  const [name, setName] = React.useState(props.name);

  const [profileImageSrc, setProfileImageSrc] = React.useState(
    props.profileImageSrc ?? DefaultAvatar,
  );

  const [hasChanged, setHasChanged] = React.useState(false);

  const handleUploadPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files !== null) {
      void props.onUploadImage(e.target.files[0]);
      setProfileImageSrc(URL.createObjectURL(e.target.files[0]));
      setHasChanged(true);
    }
  };

  const handleUpdateName = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setName(e.target.value);
  };

  const handleSubmit = async () => {
    await onUpdateProfile({
      name: name !== props.name ? name : undefined,
    });
  };

  React.useEffect(() => {
    if (name !== props.name) {
      setHasChanged(true);
    }
  }, [name, props.name]);

  return (
    <ThemeProvider theme={customTheme}>
      <div className={styles.profileContainer}>
        <div className={styles.profileHeader}>
          <h1 className={styles.heading}>your profile</h1>
        </div>
        <div className={styles.profileInfoContainer}>
          <div className={styles.profileImgContainer}>
            <input
              id="upload-profile-photo"
              type="file"
              accept="image/jpeg, image/png"
              onChange={handleUploadPhoto}
              hidden
            />
            <Avatar
              alt="profile image"
              src={profileImageSrc}
              className={classes.profileImage}
            />
            <IconButton
              color="secondary"
              className={classes.addButton}
              disabled={loading}
            >
              <label
                htmlFor="upload-profile-photo"
                className={styles.uploadProfileImageButton}
              >
                <AddIcon className={classes.addIcon} />
              </label>
            </IconButton>
          </div>
          <div className={styles.profileDetails}>
            <div className={styles.row}>
              <p className={styles.profileInfoText}>Name</p>
              <input
                type="text"
                onChange={handleUpdateName}
                value={name}
                className={styles.profileInput}
                autoComplete="name"
              />
            </div>
            <div className={styles.row}>
              <p className={styles.profileInfoText}>Password</p>
              <Button
                variant="contained"
                color="primary"
                classes={{ root: classes.buttons }}
                onClick={onClickChangePassword}
                disabled={loading}
              >
                change password
              </Button>
            </div>
            <div className={styles.row}>
              <p className={styles.profileInfoText}>2FA</p>
              <Button
                variant="contained"
                color="primary"
                className={classes.buttons}
                onClick={() => {
                  if (mfaActive) {
                    onDisableMfa();
                  } else {
                    onInitMfa();
                  }
                }}
                disabled={loading}
              >
                {mfaActive ? 'disable 2fa' : 'enable 2fa'}
              </Button>
            </div>
            <CustomButton
              onClick={handleSubmit}
              color={Colors.COLOR_SCHEME_2}
              className={styles.save}
              disabled={!hasChanged || loading}
            >
              save
            </CustomButton>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};
